import React, { useState } from 'react';
import { createPortal } from 'react-dom';
import './styles.css';
import pinImg from '../../images/pin.png';
import externalLinkImg from '../../images/external.png';
import virtualCareImg from '../../images/virtualCareIcon.svg';
import boardCertifiedImg from '../../images/star.png';
import handicapImg from '../../images/wheelchair.png';
// Use the same person icon as the summary card's "Accepting new patients" badge
// (providerSummary.jsx), instead of the previous checkmark (tick.png), for visual
// consistency between the two badges representing the same status.
import acceptingNewPatientsImg from '../../images/personOutline.svg';
import babyIcon from '../../images/babyIcon.svg';
import trainIcon from '../../images/train.svg';
import globeIcon from '../../images/globe.svg';
import { usePlanFeatures } from '../../contexts/PlanFeaturesContext';
import HandicapAccessibilityModal from '../handicapAccessibilityModal/handicapAccessibilityModal';
import LanguageModal from '../languageModal/languageModal';
import ServiceAreaModal from '../serviceAreaModal/serviceAreaModal';
import ExternalLinkModal from '../externalLinkModal/externalLinkModal';
import InfoTooltip from '../infoTooltip/infoTooltip';

// Format phone number to 123-456-7890 format
const formatPhoneNumber = (phone) => {
  if (!phone) return '';
  // Remove all non-digit characters
  const cleaned = phone.replace(/\D/g, '');
  // Format as XXX-XXX-XXXX
  if (cleaned.length === 10) {
    return `${cleaned.slice(0, 3)}-${cleaned.slice(3, 6)}-${cleaned.slice(6)}`;
  }
  // Return original if not 10 digits
  return phone;
};

// Some API responses send the literal string "null"/"Null"/"NULL" (or an empty/
// whitespace-only string) instead of an actual JS null/undefined for missing URL
// fields — a plain `url && url !== null` check doesn't catch those string variants, so
// the website link would render (and try to navigate to "https://null") instead of
// falling back to "No website found". Normalize and reject all of null/undefined/''/
// whitespace-only/"null" in any casing. Mirrors providerSummary.jsx's isValidUrl.
const isValidUrl = (url) => {
  if (url === null || url === undefined) return false;
  const trimmed = String(url).trim();
  if (trimmed === '') return false;
  if (trimmed.toLowerCase() === 'null') return false;
  return true;
};

// Public transportation access is a 3-state field per the SOT — Yes / No / no data —
// not a simple boolean, so (unlike e.g. handicapAccess/virtualCare above) it must always
// render one of three exact labels rather than being hidden whenever it isn't "Yes".
const getPublicTransitText = (pubTrans) => {
  const normalized = (pubTrans || '').trim().toUpperCase();
  if (normalized === 'YES' || normalized === 'Y') return 'Easy access to public transportation';
  if (normalized === 'NO') return 'No easy access to public transportation';
  return 'No easy access to public transportation found'; // blank/missing/unrecognized value
};

// Ordered list of the week's days, paired with the API field names that hold each
// day's from/to hours. Drives the hours-of-operation schedule below — previously that
// schedule was two entirely separate blocks of text (one for all 7 day labels, one for
// all 7 time ranges) stacked with <br> tags and relying on both blocks wrapping
// identically to stay visually aligned line-for-line. On mobile, where a single time
// range ("8:00 AM to 5:00 PM") is often too wide to fit on one line, that time-column
// text would wrap onto a second line while the day-column text didn't — permanently
// throwing every subsequent day/time pair out of alignment with each other. Rendering
// each day+time as its own paired row (below) makes that desync structurally
// impossible: a day and its hours can only ever wrap together, never apart.
const HOURS_SCHEDULE_DAYS = [
  { key: 'monday', full: 'Monday', abbr: 'Mon', fromKey: 'fromMonday', toKey: 'toMonday' },
  { key: 'tuesday', full: 'Tuesday', abbr: 'Tue', fromKey: 'fromTuesday', toKey: 'toTuesday' },
  { key: 'wednesday', full: 'Wednesday', abbr: 'Wed', fromKey: 'fromWednesday', toKey: 'toWednesday' },
  { key: 'thursday', full: 'Thursday', abbr: 'Thu', fromKey: 'fromThursday', toKey: 'toThursday' },
  { key: 'friday', full: 'Friday', abbr: 'Fri', fromKey: 'fromFriday', toKey: 'toFriday' },
  { key: 'saturday', full: 'Saturday', abbr: 'Sat', fromKey: 'fromSaturday', toKey: 'toSaturday' },
  { key: 'sunday', full: 'Sunday', abbr: 'Sun', fromKey: 'fromSunday', toKey: 'toSunday' },
];

// Strips a redundant leading zero from 12-hour clock times (e.g. "09:00 AM" -> "9:00
// AM"). That leading zero only ever occurs for the single-digit hours 01-09 (never for
// 10/11/12), so it carries no information — on the already-tight mobile hours-of-
// operation layout it was pure wasted width contributing to the "text running into each
// other" crowding.
const formatHourTime = (time) => (time || '').replace(/^0(\d:)/, '$1');


const ProviderServiceDetails = ({ serviceLocations, providerLanguages, provider, providerDetailsType, onIncorrectInfoClick, isExpanded = true }) => {
  const { isServiceLocationFieldEnabled, isProviderDetailsFieldEnabled } = usePlanFeatures();
  // Links and buttons under "Location details" should only be reachable by keyboard
  // while this accordion section is actually expanded — the shared .expandable-content
  // accordion collapses via max-height:0/overflow:hidden rather than display:none (see
  // results/styles.css), so its contents stayed in the tab order and could be
  // silently focused while still visually hidden/collapsed. Applied to every
  // interactive element rendered below (distance/phone/website links, "Check service
  // area", "Accessibility Accommodations", "Check other languages", the virtual care
  // tooltip, and "Tell us about incorrect info").
  const linkTabIndex = isExpanded ? 0 : -1;
  const [showHandicapModal, setShowHandicapModal] = useState(false);
  const [selectedLocation, setSelectedLocation] = useState(null);
  const [showLanguageModal, setShowLanguageModal] = useState(false);
  const [selectedLanguages, setSelectedLanguages] = useState([]);
  // Service area link/modal is Type 4 only — same "Check service area" county-list
  // modal already shown under the address block on the main summary card
  // (providerSummary.jsx), reused here under each individual service location's
  // own address block for the same provider.
  const [showServiceAreaModal, setShowServiceAreaModal] = useState(false);
  const handleOpenServiceAreaModal = () => setShowServiceAreaModal(true);
  const handleCloseServiceAreaModal = () => setShowServiceAreaModal(false);

  // "You are now leaving..." confirmation modal for each location's external website
  // link — same pattern/component used for providerDetailsType2.jsx's external links
  // (accreditation entries, Hospital Quality Data).
  const [externalLinkModalOpen, setExternalLinkModalOpen] = useState(false);
  const [pendingExternalUrl, setPendingExternalUrl] = useState(null);

  const handleWebsiteLinkClick = (e, url) => {
    e.preventDefault();
    setPendingExternalUrl(url);
    setExternalLinkModalOpen(true);
  };

  const handleExternalLinkConfirm = () => {
    if (pendingExternalUrl) {
      window.open(pendingExternalUrl, '_blank', 'noopener,noreferrer');
    }
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  };

  const handleExternalLinkCancel = () => {
    setExternalLinkModalOpen(false);
    setPendingExternalUrl(null);
  };
  
  // Parse provider languages string into array
  const languageArray = providerLanguages 
    ? providerLanguages.split(',').map(lang => lang.trim()).filter(lang => lang.length > 0)
    : [];
  
  const handleOpenHandicapModal = (location) => {
    setSelectedLocation(location);
    setShowHandicapModal(true);
  };
  
  const handleCloseHandicapModal = () => {
    setShowHandicapModal(false);
    setSelectedLocation(null);
  };

  const handleOpenLanguageModal = (languages) => {
    setSelectedLanguages(languages);
    setShowLanguageModal(true);
  };

  const handleCloseLanguageModal = () => {
    setShowLanguageModal(false);
    setSelectedLanguages([]);
  };
  
  /**
   * Check if a service location should display "24 hours" indicator
   * Based on open24Hours field in API response
   * @param {Object} location - Service location object
   * @returns {boolean} - True if should display "24 hours"
   */
  const isOpen24Hours = (location) => {
    // Check for open24Hours field - any non-empty value indicates 24-hour operation
    // API returns empty string for non-24-hour locations, or a value (like "Y", "Yes", etc.) for 24-hour locations
    return location?.open24Hours && location.open24Hours.trim() !== '';
  };

  // Treat "NA", "N/A", "na", "n/a" as no-hours
  const isNA = (val) => !val || /^n\/?a$/i.test(val.trim());
  const validHours = (from, to) => !isNA(from) && !isNA(to) && from && to;
  // Whether a location has at least one day with real (non-NA/non-empty) hours.
  // Used to decide between rendering the full Mon-Sun schedule (with "Closed"
  // for any individual day that lacks hours) vs. the location having no hours
  // data at all, in which case the whole block should read "None found" —
  // previously this checked `location.fromMonday || ...` for ANY truthy value,
  // but the string "NA" is truthy, so a location where every day was "NA"
  // still passed this check, rendered the full 7-row schedule, and showed
  // "Closed" for every single day instead of the single "None found" state
  // that a location with genuinely no hours data (null/undefined) already got.
  const hasAnyValidHours = (location) => HOURS_SCHEDULE_DAYS.some(({ fromKey, toKey }) => validHours(location[fromKey], location[toKey]));
  
  // Handle empty/null service locations
  if (!serviceLocations || serviceLocations.length === 0) {
    return (
      <div className="provider-service-details-section">
        <p>No locations available.</p>
      </div>
    );
  }

  // Case 1 (single location, no per-location name): Type 2/3 (hospital/facility)
  // providers commonly have exactly one service location whose own affiliation/
  // provName comes back blank from the API (e.g. "EINSTEIN MEDICAL CENTER
  // MONTGOMERY" — reported as "showing nothing in Location details"), even
  // though the provider's overall name IS that facility's name (for these
  // provider types, the org name is carried in provider.lastName/firstName —
  // see providerSummary.jsx's identical `${firstName} ${lastName}` convention
  // used for the card's own header). With only one location, there's no
  // ambiguity about which location that name belongs to, so it's safe to copy
  // the overall provider name down as this single location's display name
  // instead of leaving it blank.
  const isSingleLocation = serviceLocations.length === 1;
  const overallProviderName = `${provider?.firstName || ''} ${provider?.lastName || ''}`.trim();
  // Case 2 (multiple locations, no per-location names): with more than one
  // location there's no single unambiguous name to copy down to all of them,
  // so per the requested rule the "1/2/3..." number inside the circle badge
  // (which exists specifically to distinguish between multiple same-named/
  // unnamed locations) is only meaningful when there's more than one location
  // in the first place. The circle itself is still rendered as a visual
  // anchor either way — only the number inside it is conditionally hidden.
  // Scoped to Type 2/3 providers only, per the request — Type 1 (individual
  // provider)/Type 4 keep their existing single-location behavior unchanged.
  const hideLocationNumberBadge = isSingleLocation && (providerDetailsType === 2 || providerDetailsType === 3);

  return (
    <div className="provider-service-details-section">
      <ol className="service-locations-list">
        {serviceLocations.map((location, index) => {
          // Check if location is accepting new patients
          const isAcceptingNewPatients = location?.newAssigns === "-1" || location?.newAssigns === -1;
          
          // Build address string
          const addressParts = [
            location.provPhyAddr1,
            location.provPhyAddr2,
            location.provPhyCity || null,
            (location.provPhyState && location.provPhyZip)
              ? `${location.provPhyState} ${location.provPhyZip}`
              : null
          ].filter(Boolean);
          const addressString = addressParts.join(', ');
          const addressFormatted = addressParts.join('\n');

          // See isSingleLocation/overallProviderName comment above — only fall
          // back to the overall provider name when this is the ONE location,
          // never for one of several unnamed locations (Case 2), since there'd
          // be no way to tell them apart if they all displayed the same name.
          const displayLocationName = location.affiliation || location.provName ||
            (isSingleLocation ? overallProviderName : '');

          return (
            <li key={`location-${index}`} className="service-location-item"
aria-label={`Location ${index + 1}`}>
            <div className="frameService">
            <div className="div-wrapper">
                <div className="div">
                    {/* Case 2 rule: for a single location on Type 2/3 providers, keep the
                        circle badge itself (still a useful visual anchor at the start of
                        the row) but drop the number inside it, since a "1" is meaningless
                        when there's only ever one location to count. */}
                    <div className="group">
                        <div className="element-wrapper">
                            {!hideLocationNumberBadge && (
                              <span className="text-wrapper serviceCount">{index + 1}</span>
                            )}
                        </div>
                    </div>

                    {isServiceLocationFieldEnabled('facilityName') && (
                      <div className="div-wrapper-2" translate='no'>
                          <p className="text-wrapper">{displayLocationName}</p>
                      </div>
                    )}
                </div>
            </div>

            <div className="molecules-category">
                <div className="div-2">
                    {(isServiceLocationFieldEnabled('address') || isServiceLocationFieldEnabled('distance')) && (
                      <div className="frameService-wrapper">
                          <div className="div">
                              <div className="div-3">
                                  <div className="div-4">
                                      {isServiceLocationFieldEnabled('distance') && (
                                        <p className="div-5">
                                          {addressString && location.distanceInMiles !== null && location.distanceInMiles !== 0 ? (
                                            <>
                                              <img className="img" src={pinImg} alt="" />
                                              <a
                                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressString)}`}
                                                  rel="noopener noreferrer"
                                                  target="_blank"
                                                  tabIndex={linkTabIndex}
                                              >
                                                  <span className="span">{location.distanceInMiles} miles from you</span>
                                              </a>
                                            </>
                                          ) : (
                                            <>
                                              <img className="img" src={pinImg} alt="" />
                                              <a
                                                  href={`https://www.google.com/maps/search/?api=1&query=${encodeURIComponent(addressString)}`}
                                                  rel="noopener noreferrer"
                                                  target="_blank"
                                                  tabIndex={linkTabIndex}
                                              >
                                                  <span className="span">0 miles from you</span>
                                              </a>
                                            </>
                                          )}
                                        </p>
                                      )}

                                      {isServiceLocationFieldEnabled('address') && (
                                        <p className="element-lee-street"  translate='no'>
                                          {addressFormatted && (
                                            addressFormatted.split('\n').map((line, i) => (
                                              <React.Fragment key={`location-${index}-address-${i}`}>
                                                {line}
                                                {i < addressFormatted.split('\n').length - 1 && <br />}
                                              </React.Fragment>
                                            ))
                                          ) 
                                          // : (
                                          //   'No address found'
                                          // )
                                          }
                                          {location.provPhyCounty && (
                                            <>
                                              <br />
                                              {location.provPhyCounty} County
                                            </>
                                          )}
                                          {providerDetailsType === 4 && (
                                            <>
                                              <br />
                                              <button onClick={handleOpenServiceAreaModal} className="check-service-area-link" aria-label="Check service area" tabIndex={linkTabIndex}>
                                                Check service area
                                                <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" style={{ marginLeft: '10px' }}>
                                                  <path d="M6 12L10 8L6 4" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                                                </svg>
                                              </button>
                                            </>
                                          )}
                                        </p>
                                      )}
                                  </div>
                              </div>
                          </div>
                      </div>
                    )}

                    <div className="div-6">
                        <div className="second">
                            <div className="div">
                                <div className="div-4">
                                    {isServiceLocationFieldEnabled('phone') && (
                                      <>
                                        <div className="phone-wrapper">
                                          <p className="phone-label-inline">Phone: </p>
                                          {location.phone && location.phone !== ('null' || '') ? (
                                            <a
                                              className="div-5"
                                              href={`tel:${location.phone}`}
                                              rel="noopener noreferrer"
                                              target="_blank"
                                              tabIndex={linkTabIndex}
                                              translate='no'
                                            >
                                              {formatPhoneNumber(location.phone)}
                                            </a>
                                          ) : (
                                            <div className="div-5">No phone found</div>
                                          )}
                                        </div>
                                      </>
                                    )}

                                    {/* Fails CLOSED (defaultValue: false), matching providerSummary.jsx's
                                        card-level Fax field — a plan whose config doesn't explicitly turn
                                        on fax shouldn't suddenly see it appear here. NOTE: the API returns
                                        this flag under the `providerDetails` category (e.g.
                                        featureFlags.providerDetails.fax), not `serviceLocations`, so this
                                        must read via isProviderDetailsFieldEnabled rather than
                                        isServiceLocationFieldEnabled. */}
                                    {isProviderDetailsFieldEnabled('fax') && (
                                      <div className="phone-wrapper">
                                        <p className="phone-label-inline">Fax: </p>
                                        <div className="div-5">{location.fax ? formatPhoneNumber(location.fax) : 'None found'}</div>
                                      </div>
                                    )}

                                    {isServiceLocationFieldEnabled('website') && (
                                      <div className="div-7">
                                        <div className="text-link">
                                          <div className="lorem-ipsum-dolor">
                                            {isValidUrl(location?.url) ? (
                                              <a
                                                href={/^https?:\/\//i.test(location.url) ? location.url : `https://${location.url}`}
                                                target="_blank"
                                                rel="noopener noreferrer"
                                                onClick={(e) => handleWebsiteLinkClick(e, /^https?:\/\//i.test(location.url) ? location.url : `https://${location.url}`)}
                                                className="website-url-link"
                                                tabIndex={linkTabIndex}
                                              >
                                                <span className="website-url-text">Website</span>
                                                <img src={externalLinkImg} alt="" />
                                              </a>
                                            ) : (
                                              <span className="no-website-text">No website found</span>
                                            )}
                                          </div>
                                        </div>
                                      </div>
                                    )}
                                </div>
                            </div>
                        </div>
                    </div>

                    {isServiceLocationFieldEnabled('acceptingNewPatients') && (
                      <div className="frameService-wrapper-2">
                          
                              <div className="div-8">
                                          <img className="img" src={acceptingNewPatientsImg} alt="" />
                                  <p className="accepting-new">
                                      <span className="text-wrapper-2">
                                          {isAcceptingNewPatients ? 'Accepting new patients' : 'Not accepting new patients'}
                                          <br />
                                      </span>
                                      <span className="text-wrapper-3">
                                        {(() => {
                                          const ages = location.agesServed || location.ages;
                                          // "Not available" is the API's own null-ish placeholder for
                                          // this field (see dummyPaData.js) — a real, truthy string, so
                                          // it slipped through the old `location.agesServed || 'No ages
                                          // found'` check and rendered literally as "Not available"
                                          // instead of the intended "No ages found" empty state.
                                          return ages && ages.trim() !== '' && ages.trim().toLowerCase() !== 'not available'
                                            ? ages
                                            : 'No ages found';
                                        })()}
                                      </span>
                                  </p>
                              </div>
                          
                      </div>
                    )}
                </div>
            </div>

            <div className="frameService-wrapper-3">
                <div className="div-9">
                    {isServiceLocationFieldEnabled('hoursOfOperation') && (
                      <div className="div-wrapper">
                          <div className="text-wrapper-4">Hours of operation:</div>
                      </div>
                    )}

                    <div className="div-2">
                        <div className="div-10">
                            {isServiceLocationFieldEnabled('hoursOfOperation') && (
                              <div className="frameService-wrapper">
                                  <div className="frameService-wrapper-4">
                                      {isOpen24Hours(location) ? (
                                        // Display "24 hours" when ATR00131 is Active and value is "Yes"
                                        <div className="div-11">
                                          <div className="text-wrapper-5">
                                            24 hours
                                          </div>
                                        </div>
                                      ) : hasAnyValidHours(location) ? (
                                        // Regular hours schedule: a real <dl> (description list — the
                                        // semantically correct way to mark up a set of label/value pairs,
                                        // per the recommended a11y pattern) with each day+hours rendered as
                                        // one paired <dt>/<dd> row. This replaces the old two-parallel-
                                        // text-blocks-with-<br>-tags layout, which relied on the day column
                                        // and the hours column wrapping identically line-for-line to stay
                                        // aligned — see HOURS_SCHEDULE_DAYS above for why that broke on
                                        // mobile. Each row here is its own flex container, so a day and its
                                        // hours can only ever wrap/stack together, never desync.
                                        <dl className="hours-schedule">
                                          {HOURS_SCHEDULE_DAYS.map(({ key, full, abbr, fromKey, toKey }) => (
                                            <div className="hours-schedule-row" key={key}>
                                              <dt className="hours-schedule-day">
                                                {/* Full day name stays in the accessible tree at every
                                                    breakpoint (visually hidden, not display:none, on
                                                    mobile) — the 3-letter abbreviation shown to sighted
                                                    mobile users is aria-hidden so screen readers always
                                                    announce the unambiguous full name. */}
                                                <span className="hours-schedule-day-full">{full}</span>
                                                <span className="hours-schedule-day-abbr" aria-hidden="true">{abbr}</span>
                                              </dt>
                                              <dd className="hours-schedule-time">
                                                {validHours(location[fromKey], location[toKey]) ? (
                                                  <span>{formatHourTime(location[fromKey])} to {formatHourTime(location[toKey])}</span>
                                                ) : <span>None found</span>}
                                              </dd>
                                            </div>
                                          ))}
                                        </dl>
                                      ) : (
                                        <div className="div-11">
                                          <div className="text-wrapper-5">None found</div>
                                        </div>
                                      )}
                                  </div>
                              </div>
                            )}

                            <div className="badges-stack">
                            <div className="div-12">
                                {isServiceLocationFieldEnabled('virtualCareOffered') && (() => {
                                  // `location.virtualCare` is not a real API field — the actual data comes
                                  // through as `location.telehealth`/`location.telemedicine` (see
                                  // providerSummary.jsx's top-card `hasVirtualCare` check, which already
                                  // reads these correctly). Reading the non-existent `virtualCare` field here
                                  // meant this location-level badge always evaluated falsy and showed "No
                                  // virtual care"/"Office visits only" even when the location's own
                                  // telehealth/telemedicine value was "Yes" — contradicting the top card,
                                  // which read the same location data correctly.
                                  const hasVirtualCare = !!((location.telehealth || location.telemedicine || '').trim());
                                  return (
                                  <div className="div virtual-care-badge-row">
                                    <div className="div-4">
                                        <div className="text-wrapper-6">
                                          <img className="img" src={virtualCareImg} alt="" />
                                          {hasVirtualCare ? 'Virtual care' : 'No virtual care'}
                                          <InfoTooltip
                                            heading="Virtual care"
                                            body="Telehealth or telemedicine. Use a phone or computer to connect with your provider. This works when you don't need to see them in person."
                                            tabIndex={linkTabIndex}
                                          />
                                        </div>
                                        {/* SOT: "Office visits only" applies to ALL provider types whenever a
                                            location has no virtual care — this component (ProviderServiceDetails)
                                            is already shared across every provider type's "Location details"
                                            section, so this single addition covers all of them. Matches the
                                            equivalent line already shown on the provider summary card
                                            (providerSummary.jsx's "Office visits only" / "Some locations" pair).
                                            marginLeft: 32px shifts this to align under the badge TEXT rather
                                            than the icon — icon width (24px) + the badge row's flex gap (8px) —
                                            the exact same 32px offset used for this same text on the main card. */}
                                        {!hasVirtualCare && (
                                          <div className="text-wrapper-9" style={{ marginLeft: '32px' }}>Office visits only</div>
                                        )}
                                    </div>
                                  </div>
                                  );
                                })()}

                                {isServiceLocationFieldEnabled('handicapAccessible') && (() => {
                                  // `location.ADA.handicapAccess` is a raw 'Y'/'N' string from
                                  // the API — checking it for plain truthiness (as this used to
                                  // do) means 'N' is ALSO truthy, so a non-accessible location
                                  // would still render "Handicap accessible" here. This caused
                                  // an inconsistency with providerSummary.jsx's card-level badge
                                  // (which does compare === 'Y'), where the main card could say
                                  // "Not handicap accessible" while every location badge here
                                  // still (incorrectly) said "Handicap accessible".
                                  const isHandicapAccessible = String(location.ADA?.handicapAccess || '').toUpperCase() === 'Y';
                                  return (
                                  <div className="div handicap-badge-row">
                                    <div className="div-4">
                                        <p className="p">
                                            <img className="img mr10" src={handicapImg} alt="" />
                                            <span className="text-wrapper-7">
                                                {isHandicapAccessible ? 'Handicap accessible' : 'Not handicap accessible'} <br />
                                            </span>
                                            {/* Previously only shown for accessible locations — QA (8/3) confirmed
                                                the modal is expected to appear for NOT-accessible locations too, so
                                                users can still see whatever accommodation details the location does
                                                have on record instead of the link vanishing outright when the
                                                headline badge says "Not handicap accessible". */}
                                            <button
                                                onClick={() => handleOpenHandicapModal(location)}
                                                className="check-handicap-link"
                                                style={{ marginLeft: '34px' }}
                                                aria-label="Check accessibility accommodations"
                                                tabIndex={linkTabIndex}
                                            >
                                                Accessibility accommodations
                                            </button>
                                        </p>
                                    </div>
                                  </div>
                                  );
                                })()}
                                {isServiceLocationFieldEnabled('birthFriendly') && location.birthFriendly && (
                                  <div className="div birth-friendly-badge-row">
                                    <div className="div-4">
                                        <p className="p">
                                            <img className="img mr10" src={babyIcon} alt="" />
                                            <span className="text-wrapper-7">
                                                Birthing-friendly care
                                            </span>
                                        </p>
                                    </div>
                                  </div>
                                )}
                            </div>

                            <div className="div-13">
                                  {isServiceLocationFieldEnabled('publicTransportation') && (
                      <div className="frameService-wrapper-2 public-transit-badge-row">
                          <div className="div-8 public-transit-badge">
                              <img className="img" src={trainIcon} alt="" />
                              <p className="public-transit-text">
                                  <span className="text-wrapper-public-transit">
                                      {getPublicTransitText(location.ADA?.pubTrans)}
                                  </span>
                              </p>
                          </div>
                      </div>
                    )}

                    {isServiceLocationFieldEnabled('allProvidersEnglish') && languageArray.length > 0 && (
                      <div className="frameService-wrapper-2 language-badge-wrapper">
                          <div className="div-8 language-badge">
                              <img className="img" src={globeIcon} alt="" />
                              <div className="language-badge-text">
                                  <span className="language-text-sentence-1">
                                      All providers speak English and offer interpreter services.{' '}
                                  </span>
                                  <button 
                                    className="language-link-button"
                                    onClick={() => handleOpenLanguageModal(languageArray)}
                                    aria-label="Check other languages"
                                    tabIndex={linkTabIndex}
                                  >
                                    Check other languages.
                                  </button>
                                  <span className="language-text-sentence-3">
                                    {' '}Call providers directly to learn more about these services.
                                  </span>
                              </div>
                          </div>
                      </div>
                    )}
                            </div>
                            </div> {/* end badges-stack */}
                        </div>
                    </div>
                </div>
            </div>

            {/* Incorrect Info Link for this location */}
            {onIncorrectInfoClick && (
              <div className="incorrect-info-link-wrapper">
                <button 
                  type="button"
                  className="incorrect-info-link"
                  onClick={() => onIncorrectInfoClick(provider, location)}
                  aria-label={`Tell us about incorrect information for location ${index + 1}`}
                  tabIndex={linkTabIndex}
                >
                  Tell us about incorrect info
                  <svg width="16" height="16" viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
                    <path d="M6 3L11 8L6 13" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round"/>
                  </svg>
                </button>
              </div>
            )}
            </div>
            
            {/* Separator between locations */}
            {index < serviceLocations.length - 1 && (
              <hr className="location-separator" />
            )}
          </li>
        );
      })}
      </ol>
      
      {/* Handicap Accessibility Modal */}
      {showHandicapModal && selectedLocation && (
        <HandicapAccessibilityModal
          isOpen={showHandicapModal}
          onClose={handleCloseHandicapModal}
          location={selectedLocation}
        />
      )}

      {/* Language Modal */}
      {showLanguageModal && (
        <LanguageModal
          isOpen={showLanguageModal}
          onClose={handleCloseLanguageModal}
          languages={selectedLanguages}
        />
      )}

      {/* Service Area Modal — shared across all of this provider's locations,
          same as providerSummary.jsx's single instance; the county list it
          shows is derived from provider.locations as a whole, not any one
          specific location, so a single shared modal is correct here too. */}
      {showServiceAreaModal && (
        <ServiceAreaModal
          isOpen={showServiceAreaModal}
          onClose={handleCloseServiceAreaModal}
          provider={provider}
        />
      )}

      {externalLinkModalOpen && createPortal(
        // Portaled to document.body — this component renders inside .results-list,
        // which has `transform: translateZ(0)` (see results/styles.css) to work around
        // an Edge/Chromium compositor bug. A CSS transform on an ancestor creates a new
        // containing block for `position: fixed` descendants, so without a portal this
        // modal's fixed overlay would be positioned relative to .results-list instead of
        // the viewport — rendering it off-position/invisible, appearing as if clicking
        // the website link did nothing. Matches the same portal pattern already used for
        // this modal in providerSummary.jsx/providerDetailsType2.jsx/hoverTooltip.jsx.
        <ExternalLinkModal
          isOpen={externalLinkModalOpen}
          onConfirm={handleExternalLinkConfirm}
          onCancel={handleExternalLinkCancel}
          url={pendingExternalUrl}
        />,
        document.body
      )}
    </div>
  );
};

export default ProviderServiceDetails;
